import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  FundCall,
  FundCallInsert,
  FundCallUpdate,
  FundCallItem,
  FundCallBudget,
  FundCallBudgetInsert,
  FundCallItemDetail,
} from '../types'

const QUERY_KEY = 'fund_calls'

export function useFundCalls(residenceId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, residenceId, fiscalYearId],
    queryFn: async () => {
      let query = supabase
        .from('fund_calls')
        .select(`
          *,
          fiscal_year:fiscal_years(id, name),
          fund_call_budgets(
            id,
            budget_id,
            amount,
            budget:budgets(id, name, budget_type, total_amount)
          )
        `)
        .order('call_date', { ascending: false })

      if (residenceId) {
        query = query.eq('residence_id', residenceId)
      }
      if (fiscalYearId) {
        query = query.eq('fiscal_year_id', fiscalYearId)
      }

      const { data, error } = await query
      if (error) throw error
      return data as FundCall[]
    },
    enabled: !!residenceId,
  })
}

export function useFundCall(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fund_calls')
        .select(`
          *,
          fiscal_year:fiscal_years(id, name),
          fund_call_budgets(
            id,
            budget_id,
            amount,
            budget:budgets(id, name, budget_type, total_amount)
          ),
          fund_call_items(
            *,
            owner:owners(id, first_name, last_name, email),
            unit:units(id, unit_number, stairwell)
          )
        `)
        .eq('id', id!)
        .single()

      if (error) throw error
      return data as FundCall & {
        fund_call_budgets: FundCallBudget[]
        fund_call_items: FundCallItem[]
      }
    },
    enabled: !!id,
  })
}

interface CreateFundCallWithBudgetsParams {
  fundCall: Omit<FundCallInsert, 'budget_id'>
  budgets: Array<{ budget_id: string; amount: number }>
  generateItems?: boolean // Si true, génère les lignes par copropriétaire selon les clés de répartition
}

export function useCreateFundCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ fundCall, budgets, generateItems = true }: CreateFundCallWithBudgetsParams) => {
      // 1. Créer l'appel de fonds (sans budget_id car déprécié)
      const { data: createdFundCall, error: fundCallError } = await supabase
        .from('fund_calls')
        .insert({
          ...fundCall,
          budget_id: null, // Déprécié, on utilise fund_call_budgets
        })
        .select()
        .single()

      if (fundCallError) throw fundCallError

      // 2. Créer les associations avec les budgets
      if (budgets.length > 0) {
        const fundCallBudgets: FundCallBudgetInsert[] = budgets.map((b) => ({
          fund_call_id: createdFundCall.id,
          budget_id: b.budget_id,
          amount: b.amount,
        }))

        const { error: budgetsError } = await supabase
          .from('fund_call_budgets')
          .insert(fundCallBudgets)

        if (budgetsError) throw budgetsError
      }

      // 3. Calculer et mettre à jour le montant total
      const totalAmount = budgets.reduce((sum, b) => sum + b.amount, 0)
      if (totalAmount > 0) {
        const { error: updateError } = await supabase
          .from('fund_calls')
          .update({ total_amount: totalAmount })
          .eq('id', createdFundCall.id)

        if (updateError) throw updateError
      }

      // 4. Générer les lignes par copropriétaire selon les clés de répartition
      if (generateItems) {
        const { error: generateError } = await supabase.rpc('generate_fund_call_items', {
          p_fund_call_id: createdFundCall.id,
        })

        if (generateError) {
          console.warn('Erreur lors de la génération des lignes:', generateError)
          // On ne fait pas échouer la création, on log juste l'erreur
        }
      }

      return createdFundCall as FundCall
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, data.residence_id] })
    },
  })
}

interface UpdateFundCallWithBudgetsParams {
  id: string
  fundCall: FundCallUpdate
  budgets?: Array<{ budget_id: string; amount: number }>
}

export function useUpdateFundCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, fundCall, budgets }: UpdateFundCallWithBudgetsParams) => {
      // 1. Mettre à jour l'appel de fonds
      const { data: result, error } = await supabase
        .from('fund_calls')
        .update(fundCall)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      // 2. Si les budgets sont fournis, mettre à jour les associations
      if (budgets !== undefined) {
        // Supprimer les anciennes associations
        const { error: deleteError } = await supabase
          .from('fund_call_budgets')
          .delete()
          .eq('fund_call_id', id)

        if (deleteError) throw deleteError

        // Créer les nouvelles associations
        if (budgets.length > 0) {
          const fundCallBudgets: FundCallBudgetInsert[] = budgets.map((b) => ({
            fund_call_id: id,
            budget_id: b.budget_id,
            amount: b.amount,
          }))

          const { error: insertError } = await supabase
            .from('fund_call_budgets')
            .insert(fundCallBudgets)

          if (insertError) throw insertError
        }

        // Mettre à jour le montant total
        const totalAmount = budgets.reduce((sum, b) => sum + b.amount, 0)
        await supabase
          .from('fund_calls')
          .update({ total_amount: totalAmount })
          .eq('id', id)
      }

      return result as FundCall
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Émettre un appel de fonds (passer en statut "issued")
 * Cette action génère les écritures comptables :
 * - Crédit : 701 (provisions), 102 (travaux), 105 (fonds ALUR) selon le type de budget
 * - Débit : 450xxx (comptes copropriétaires)
 */
export function useIssueFundCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      // 1. Mettre à jour le statut de l'appel de fonds
      const { data: result, error } = await supabase
        .from('fund_calls')
        .update({
          status: 'issued',
          issued_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      // 2. Récupérer les budgets associés à cet appel
      const { data: fundCallBudgets } = await supabase
        .from('fund_call_budgets')
        .select('budget_id')
        .eq('fund_call_id', id)

      // 3. Passer les budgets associés en statut 'active' (s'ils sont en 'voted')
      if (fundCallBudgets && fundCallBudgets.length > 0) {
        const budgetIds = fundCallBudgets.map((fcb) => fcb.budget_id)
        const { error: statusError } = await supabase
          .from('budgets')
          .update({ status: 'active' })
          .in('id', budgetIds)
          .eq('status', 'voted')

        if (statusError) {
          console.warn('Erreur lors de la mise à jour du statut des budgets:', statusError)
        }
      }

      // 4. Générer les écritures comptables (journal AF)
      const { data: transactionId, error: entriesError } = await supabase.rpc(
        'generate_fund_call_accounting_entries',
        { p_fund_call_id: id }
      )

      if (entriesError) {
        // Remettre l'appel en brouillon car les écritures n'ont pas pu être générées
        await supabase
          .from('fund_calls')
          .update({ status: 'draft', issued_at: null })
          .eq('id', id)

        // Propager l'erreur pour qu'elle soit affichée dans un toast
        throw new Error(`Erreur lors de la génération des écritures comptables: ${entriesError.message}`)
      }

      console.log('Écritures comptables générées, transaction ID:', transactionId)

      return result as FundCall
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['budgets'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

/**
 * Envoyer un appel de fonds aux copropriétaires (passer en statut "sent")
 * Nécessite que l'appel soit d'abord émis (status = 'issued')
 */
export function useSendFundCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: result, error } = await supabase
        .from('fund_calls')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result as FundCall
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useRecordPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      fundCallId,
      ownerId,
      amount,
      paymentDate,
    }: {
      fundCallId: string
      ownerId: string
      amount: number
      paymentDate: string
    }) => {
      const { data: result, error } = await supabase
        .from('fund_call_items')
        .update({
          paid_amount: amount,
          paid_at: paymentDate,
        })
        .eq('fund_call_id', fundCallId)
        .eq('owner_id', ownerId)
        .select()
        .single()

      if (error) throw error
      return result as FundCallItem
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useDeleteFundCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      // Les fund_call_budgets et fund_call_items sont supprimés en cascade
      const { error } = await supabase
        .from('fund_calls')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Hook pour récupérer les items d'un appel de fonds avec leurs détails de calcul
export function useFundCallItemsWithDetails(fundCallId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'items_with_details', fundCallId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fund_call_items')
        .select(`
          *,
          owner:owners(id, first_name, last_name, email),
          unit:units(id, unit_number, stairwell),
          fund_call_item_details(
            id,
            budget_line_id,
            distribution_key_id,
            budget_name,
            budget_line_label,
            distribution_key_name,
            share_value,
            total_shares,
            percentage,
            base_amount,
            calculated_amount
          )
        `)
        .eq('fund_call_id', fundCallId!)
        .order('amount', { ascending: false })

      if (error) throw error
      return data as (FundCallItem & { fund_call_item_details: FundCallItemDetail[] })[]
    },
    enabled: !!fundCallId,
  })
}

// Hook pour récupérer uniquement les détails d'un item spécifique
export function useFundCallItemDetails(fundCallItemId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'item_details', fundCallItemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fund_call_item_details')
        .select('*')
        .eq('fund_call_item_id', fundCallItemId!)
        .order('budget_name')
        .order('budget_line_label')

      if (error) throw error
      return data as FundCallItemDetail[]
    },
    enabled: !!fundCallItemId,
  })
}

// Hook pour régénérer les items d'un appel de fonds
export function useRegenerateFundCallItems() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (fundCallId: string) => {
      const { error } = await supabase.rpc('generate_fund_call_items', {
        p_fund_call_id: fundCallId,
      })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}
